import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
} from "react";

const ShownByPageContext = createContext(false);
const SetShownByPageContext = createContext<(isShown: boolean) => void>(
  () => undefined
);

interface DealOnboardingPromptProviderProps {
  children: React.ReactNode;
}

/** Lets a deal page that shows the onboarding card tell page-wide prompts to step aside. */
export const DealOnboardingPromptProvider = ({
  children,
}: DealOnboardingPromptProviderProps): JSX.Element => {
  const [isShownByPage, setShownByPage] = useState(false);

  return (
    <SetShownByPageContext.Provider value={setShownByPage}>
      <ShownByPageContext.Provider value={isShownByPage}>
        {children}
      </ShownByPageContext.Provider>
    </SetShownByPageContext.Provider>
  );
};

/** Held by the deal page for as long as it shows its onboarding card. */
export const useShowsDealOnboardingPrompt = (isShown: boolean): void => {
  const setShownByPage = useContext(SetShownByPageContext);

  // Before paint, so the banner never flashes above a card that replaces it.
  useLayoutEffect(() => {
    setShownByPage(isShown);
    return () => setShownByPage(false);
  }, [isShown, setShownByPage]);
};

export const useIsDealOnboardingPromptShown = (): boolean =>
  useContext(ShownByPageContext);
